<template>
    <Transition name="fade-slide">
    <div v-show="opened && triggered"
         :class="[positionClass, { triggered }]"
         class="tooltip">
        <div class="content">
            <slot />
        </div>
        <span v-if="closeable"
              class="close"
              @click="close"
        ></span>
    </div>
    </Transition>
</template>

<script>
import { getBoxSize } from '@dsee/util/dom.util.js';
import Timer from '@dsee/util/timer.js';

const ARROW_OFFSET_X = 20;
const ARROW_OFFSET_Y = 8;

const TRIGGER_CLICK = 'click';
const TRIGGER_HOVER = 'hover';
const TRIGGER_FOCUS = 'focus';

export default {
    name: 'TooltipOld',
    props: {
        /**
         * Цель для привязки
         */
        bindTo: {
            required: true,
        },
        /**
         * Список событий (через запятую / пробел) для привязки
         */
        triggerOn: {
            type: String,
            default: '',
        },
        /**
         * Отображать кнопку закрытия?
         */
        closeable: {
            type: Boolean,
            default: false,
        },
        /**
         * Флаг программного открытия тултипа
         */
        opened: {
            default: true,
        },
        /**
         * Флаг программного запрещения открытия тултипа
         */
        disabled: {
            default: false,
        },
        /**
         * Задержка открытия тултипа при наведении на цель
         */
        openDelay: {
            type: Number,
            default: 100,
        },
        /**
         * Задержка закрытия тултипа при уводе с цели
         */
        hideDelay: {
            type: Number,
            default: 100,
        },
        /**
         * Показывать тултип сбоку
         */
        sideways: {
            type: Boolean,
            default: false,
        },
        /**
         * Показывать тултип по центру
         */
        center: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            /**
             * Флаг срабатывания триггера
             */
            triggered: false,
            /**
             * Классы позиционирования
             */
            positionClass: [],
        };
    },
    methods: {
        /**
         * Рассчитать стиль отображения по оси X
         */
        getPositionX(targetRect) {
            if (this.center)
                return this.getPositionXByAlignCenter(targetRect);

            if (targetRect.width >= 2 * ARROW_OFFSET_X) {
                return this.getPositionXByCorner(targetRect);
            } else {
                return this.getPositionXByCenter(targetRect);
            }
        },

        /**
         * Позиционирование от угла
         */
        getPositionXByCorner(targetRect) {
            const dialogRect = this.$options.dialogRect;

            if (targetRect.left + dialogRect.width >= document.documentElement.clientWidth &&
                targetRect.right - dialogRect.width >= 0)
                return [ 'x-left', targetRect.right - dialogRect.width ];

            return [ 'x-right', targetRect.left - ARROW_OFFSET_X / 6 ];
        },

        /**
         * Позиционирование от центра
         */
        getPositionXByCenter(targetRect) {
            const dialogRect = this.$options.dialogRect;
            const dialogWidth = dialogRect.width - ARROW_OFFSET_X;
            const targetCenter = (targetRect.left + targetRect.right) / 2;

            if (targetCenter + dialogWidth >= document.documentElement.clientWidth &&
                targetCenter - dialogWidth >= 0)
                return [ 'x-left', targetCenter - dialogWidth ];

            return [ 'x-right', targetCenter - ARROW_OFFSET_X ];
        },

        /**
         * Центрирование
         */
        getPositionXByAlignCenter(targetRect) {
            const dialogRect = this.$options.dialogRect;
            const dialogWidth = dialogRect.width;
            const targetCenter = (targetRect.left + targetRect.right) / 2;

            return [ 'x-center', targetCenter - (dialogWidth / 2) ];
        },

        /**
         * Рассчитать стиль отображения по оси Y
         */
        getPositionY(targetRect) {
            const dialogRect = this.$options.dialogRect;
            const dialogHeight = dialogRect.height + ARROW_OFFSET_Y;

            if (targetRect.bottom + dialogHeight >= document.documentElement.clientHeight &&
                targetRect.top - dialogHeight >= 0)
                return [ 'y-top', targetRect.top - dialogHeight ];

            return [ 'y-bottom', targetRect.bottom + ARROW_OFFSET_Y ];
        },

        /**
         * Обновить положение тултипа на странице
         */
        updatePosition() {
            const targetRect = this.$options.target.getBoundingClientRect();

            const [ styleX, pageX ] = this.getPositionX(targetRect);
            const [ styleY, pageY ] = this.getPositionY(targetRect);

            this.positionClass = [ styleX, styleY ];
            this.$el.style.left = (window.pageXOffset + pageX) + 'px';
            this.$el.style.top = (window.pageYOffset + pageY) + 'px';
        },

        /**
         * Вычислить z-index относительно тригера
         */
        updateZindex() {
            let item = this.$options.target;

            while (item) {
                const { zIndex } = getComputedStyle(item);

                if (zIndex !== 'auto') {
                    this.$el.style.zIndex = parseInt(zIndex) + 1;
                    return;
                }

                item = item.parentElement;
            }
        },

        /**
         * Программное открытие тултипа
         */
        syncOpened() {
            if (this.opened) {
                this.open();
            } else {
                this.close();
            }
        },

        /**
         * Открыть подсказку
         */
        async open() {
            if (this.disabled) return;

            this.clearTimers();
            await this.updatePosition();
            this.updateZindex();
            this.triggered = true;
            this.$emit('open');
        },

        /**
         * Закрыть подсказку
         */
        close() {
            this.clearTimers();
            this.triggered = false;
            this.$emit('close');
        },

        /**
         * Сбросить таймеры
         */
        clearTimers() {
            this.$options.openTimer.clearTimeout();
            this.$options.hideTimer.clearTimeout();
        },

        /**
         * Обработка события наведения на область цели / тултипа
         */
        enter() {
            this.$options.hideTimer.clearTimeout();

            if (!this.triggered) {
                this.$options.openTimer.setTimeoutIfAbsent(
                    this.open,
                    this.openDelay,
                );
            }
        },

        /**
         * Обработка события выхода из области цели / тултипа
         */
        leave() {
            this.$options.openTimer.clearTimeout();

            if (this.triggered && this.hideDelay) {
                this.$options.hideTimer.setTimeoutIfAbsent(
                    this.close,
                    this.hideDelay,
                );
            }
        },

        /**
         * Обработка событий вне компонента
         */
        clickOutside(e) {
            if (this.triggered && !this.insideTooltip(e.target)) {
                this.close();
            }
        },

        /**
         * Событие произошло внутри тултипа или триггера
         */
        insideTooltip(target) {
            return this.$el.contains(target) || this.$options.target.contains(target);
        },
        /**
         * Найти цель привязки
         */
        findTargetElement() {
            let parent = this.$parent;

            if (parent == null)
                throw new Error('Parent component does not exist');

            while (parent != null) {
                const target = parent.$el.querySelector(this.bindTo);

                if (target != null)
                    return target;

                parent = parent.$parent;
            }

            throw new Error('Target element ' + this.bindTo + ' is not found in parent context');
        },
    },
    async mounted() {
        await this.$nextTick();

        if (this.$parent == null)
            throw new Error('Parent component does not exist');

        // цель привязки
        const target = this.$options.target = this.findTargetElement();

        // меняем родителя на корень
        this.$el.parentNode.removeChild(this.$el);
        document.body.appendChild(this.$el);

        // размеры диалога
        this.$options.dialogRect = getBoxSize(this.$el);

        const triggerOn = this.$options.triggerOn = new Set();

        // подписываемся на события открытия
        for (const event of this.triggerOn.split(/[;,\s]+/)) {
            if (!event || triggerOn.has(event))
                continue;

            if (event === TRIGGER_CLICK) {
                target.addEventListener('click', this.open);
            } else if (event === TRIGGER_HOVER) {
                target.addEventListener('mouseenter', this.enter);
                target.addEventListener('mouseleave', this.leave);
                this.$el.addEventListener('mouseenter', this.enter);
                this.$el.addEventListener('mouseleave', this.leave);
            } else if (event === TRIGGER_FOCUS) {
                target.addEventListener('focus', this.open);
                target.addEventListener('blur', this.close);
            } else {
                throw new Error('Trigger event `' + event + '` is not supported');
            }

            triggerOn.add(event);
        }

        if (triggerOn.has(TRIGGER_CLICK) || triggerOn.has(TRIGGER_HOVER)) {
            document.addEventListener('click', this.clickOutside, true);
        }

        if (triggerOn.size === 0) {
            this.$watch('opened', this.syncOpened);
            this.syncOpened();
        }

        this.$watch('disabled', () => {
            if (this.disabled && this.triggered)
                this.close();
        });

        window.addEventListener('resize', () => {
            if (this.triggered)
                this.updatePosition();
        });
        document.addEventListener('scroll', () => {
            if (this.triggered)
                this.updatePosition();
        });
    },
    created() {
        this.$options.openTimer = new Timer();
        this.$options.hideTimer = new Timer();
    },
    destroyed() {
        if (document.body.contains(this.$el))
            document.body.removeChild(this.$el);

        document.removeEventListener('click', this.clickOutside, true);
        window.removeEventListener('resize', this.updatePosition);
        document.removeEventListener('scroll', this.updatePosition, true);

        this.clearTimers();
    },
};
</script>

<style lang="scss" scoped>
@import '~@dsee/mixins.scss';

.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: var(--transition-function);
}

.fade-slide-enter,
.fade-slide-leave-to {
    transform: translateY(15px);
    opacity: 0;
}

.tooltip {
    --background-color: rgba(21, 29, 44, 1);
    --border-color: var(--background-color);

    position: absolute;
    z-index: 1;
    display: flex;
    box-sizing: border-box;
    padding: var(--input-padding);
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--background-color);
    margin: 0;
    max-width: 400px;
    min-width: 40px;
    font-family: var(--text-font-family);
    font-size: 12px;
    line-height: 16px;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
    letter-spacing: 0.01em;
    filter: drop-shadow(0px 12px 20px rgba(28, 41, 61, 0.06)) drop-shadow(0px 0px 20px rgba(28, 41, 61, 0.05));
    transition: var(--transition-function);

    & > .close {
        @include icon(16px, 16px);
        background-image: url('./icons/close.svg');
        margin-left: 8px;
        opacity: 0.5;
    }

    &::after,
    &::before {
        position: absolute;
        border: 0 solid transparent;
        content: "";
        height: 0;
        width: 0;
    }

    &::before {
        border-width: 8px;
    }

    &::after {
        border-width: 7px;
    }

    &.y-bottom {
        &::before {
            border-bottom-color: var(--border-color);
            bottom: 100%;
        }

        &::after {
            border-bottom-color: var(--background-color);
            bottom: 100%;
        }
    }

    &.y-top {
        &::before {
            border-top-color: var(--border-color);
            top: 100%;
        }

        &::after {
            border-top-color: var(--background-color);
            top: 100%;
        }
    }

    &.x-right {
        &::before {
            left: 12px;
        }

        &::after {
            left: 13px;
        }
    }

    &.x-left {
        &::before {
            right: 12px;
        }

        &::after {
            right: 13px;
        }
    }

    &.x-center {
        &::before {
            left: calc(50% - 8px);
        }

        &::after {
            left: calc(50% - 7px);
        }
    }

    & > .content {
        width: 100%;
        overflow-wrap: anywhere;
    }
}
</style>

<style lang="scss">
.tooltip {
    &.error {
        --background-color: #FFCACA;
        --border-color: var(--background-color);
        font-size: 14px;
        line-height: 16px;
        font-weight: 500;
        letter-spacing: 0.005em;
        color: var(--text-color);
    }

    &.hint {
        --background-color: rgba(249, 249, 251, 1);
        --border-color: rgba(16, 24, 40, 0.25);
        color: var(--text-color);
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.005em;
        padding: 4px 8px;
    }

    &.inform {
        --background-color: var(--option-dialog-background);
        color: var(--text-color);
        box-shadow: 0px 12px 20px 0px rgba(28, 41, 61, 0.06), 0px 0px 20px 0px rgba(28, 41, 61, 0.05);
        border-radius: 16px;
        filter: none;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.005em;

        &:not(.small) {
            padding: 20px 32px;
        }
    }
}
</style>
